/** `code` for the matched-to-existing-account failure shape (see below). */
export const ALREADY_MATCHED_CODE = 'ALREADY_MATCHED';

/**
 * Failure body returned by `POST /v1/auth/signup` (createUser). The backend
 * signals an existing account in TWO distinct shapes — the view branches on
 * exactly these, mirroring Sparta's two AccountAlreadyExisted branches:
 *
 *   1. Matched to an existing/known account — a stable machine `code` plus a
 *      masked email of the account it matched (so the user can recognize it).
 *      As sent on the wire (see camelization note below):
 *        { "code": "ALREADY_MATCHED", "masked_email": "b•••@vida.com" }
 *
 *   2. Plain email-uniqueness violation — only a human-readable `error` message,
 *      with NO code and NO masked email:
 *        { "error": "A user with that email address already exists" }
 *
 * Any other failure carries none of these reliably and should surface generically.
 *
 * NOTE: error responses skip the camelize interceptor — `authClient` only
 * camelizes *fulfilled* responses — so the masked email arrives snake-cased as
 * `masked_email`. `getCreateUserError` normalizes it; the type below is the
 * normalized (camel) shape callers see.
 */
export type CreateUserErrorData = {
  code?: string;
  error?: string;
  maskedEmail?: string;
};

/**
 * Pull the typed createUser error body off an unknown axios error
 * (`error.response.data`). Returns `undefined` when the error isn't shaped like
 * an API response, so the view can fall through to a generic message.
 */
export function getCreateUserError(error: unknown): CreateUserErrorData | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const body = data as Record<string, unknown>;
  // Error bodies bypass the camelize interceptor, so accept the raw `masked_email`
  // (and a camel fallback, in case it's ever pre-camelized). `code`/`error` are
  // single words, unaffected by casing.
  const maskedEmail = body.maskedEmail ?? body.masked_email;
  return {
    code: typeof body.code === 'string' ? body.code : undefined,
    error: typeof body.error === 'string' ? body.error : undefined,
    maskedEmail: typeof maskedEmail === 'string' ? maskedEmail : undefined,
  };
}
